import type { CSSProperties } from 'react';
import { useStore, selectActiveNodes, isMechanicalItemNode } from '../store';

/**
 * Fixed info panel for the currently-selected mechanical item. Lives in the
 * canvas overlay layer (above the React-Flow viewport) and sits at a fixed
 * top-left spot, so the selected part's name/dimensions are ALWAYS readable and
 * never hidden behind a neighbouring footprint.
 */
export function MechanicalSelectionInfo() {
  const selectedId = useStore((s) => s.selectedNodeId);
  const nodes = useStore(selectActiveNodes);
  const node = nodes.find((n) => n.id === selectedId);
  if (!node || !isMechanicalItemNode(node)) return null;

  const d = node.data;
  const label = (d.label ?? '').trim() || 'Mechanical item';
  const orientation = d.orientation ?? 'top';
  const widthMm = d.widthMm ?? 100;
  const heightMm = d.heightMm ?? 60;
  const depthMm = d.depthMm ?? 20;
  const detail = `${widthMm} × ${heightMm} mm${d.depthMm ? ` × ${d.depthMm}` : ''}`;
  const fp =
    orientation === 'side'
      ? { w: depthMm, h: heightMm }
      : orientation === 'front'
        ? { w: widthMm, h: depthMm }
        : { w: widthMm, h: heightMm };
  const rotation = normalizeRotation(d.rotation);
  const sideways = rotation === 90 || rotation === 270;
  const footprintDetail = `${round(sideways ? fp.h : fp.w)} × ${round(sideways ? fp.w : fp.h)} mm`;

  return (
    <div style={panelStyle}>
      <div style={nameStyle}>{label}</div>
      <div style={metaStyle}>{detail}</div>
      <div style={metaStyle}>
        {orientationLabel(orientation)} · footprint {footprintDetail}
        {rotation ? ` · ${rotation}°` : ''}
      </div>
    </div>
  );
}

function round(value: number) {
  return Math.round(value * 10) / 10;
}

function orientationLabel(value: unknown) {
  if (value === 'side') return 'Side mount';
  if (value === 'front') return 'Front mount';
  return 'Top view';
}

function normalizeRotation(value: unknown) {
  const numeric = typeof value === 'number' ? value : 0;
  return ((((Math.round(numeric / 90) * 90) % 360) + 360) % 360) as 0 | 90 | 180 | 270;
}

const panelStyle: CSSProperties = {
  position: 'absolute',
  left: 12,
  top: 12,
  zIndex: 12,
  maxWidth: 260,
  padding: '7px 10px',
  background: 'var(--panel)',
  border: '1px solid var(--accent-border)',
  borderLeft: '3px solid var(--accent)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow)',
  pointerEvents: 'none',
};

const nameStyle: CSSProperties = {
  fontSize: 12.5,
  fontWeight: 800,
  color: 'var(--text)',
  lineHeight: 1.2,
  wordBreak: 'break-word',
};

const metaStyle: CSSProperties = {
  marginTop: 2,
  fontSize: 10.5,
  color: 'var(--text-muted)',
  fontFamily: 'ui-monospace, Consolas, monospace',
  lineHeight: 1.25,
};
